import type { DateTime } from "luxon";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useCallback, useMemo } from "react";
import { getDefaultMRTRowEditModeOptions } from "../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMinimalMRTOptions } from "../../../mantine/getMinimalMRTOptions";
import type { ChangeableFieldData } from "../ChangeableFieldWidget";
import "./ChangeableFieldTable.scss";
import { getDefaultValueColumn } from "./columns/getDefaultValueColumn";
import { getEndDateColumn } from "./columns/getEndDateColumn";
import { getStartDateColumn } from "./columns/getStartDateColumn";

/** Per-row field errors from the API, indexed by row position. */
export type ChangeableFieldErrors = Array<Record<string, Array<string>>>;

export interface ChangeableFieldTableOptions {
  startDateHelpText?: string;
  startDateIsRequired?: boolean;
  startDatePlaceholder?: string;
  endDatePlaceholder?: string;
  allowDeletionOfAllRows?: boolean;
  restrictFutureDates?: boolean;
  deleteTooltipText?: string;
  numberValueHeader?: string;
  numberValueDecimalScale?: number;
  inputGroupText?: string;
  inputGroupTextWidth?: number;
  dateCellWidth?: number;
  isTextField?: boolean;
}

export type ValueColumns<T> = Array<
  Omit<MRT_ColumnDef<ChangeableFieldData<T>>, "Cell"> & {
    Cell: (
      props: Parameters<
        NonNullable<MRT_ColumnDef<ChangeableFieldData<T>>["Cell"]>
      >[0],
      handleChangeValue: (index: number, value: T) => void
    ) => React.ReactNode;
  }
>;

interface ChangeableFieldTableProps<T> {
  values: Array<ChangeableFieldData<T>>;
  dataName: string;
  tableOptions?: ChangeableFieldTableOptions;
  valueColumns?: ValueColumns<T>;
  onUpdateValue: (index: number, updatedValue: ChangeableFieldData<T>) => void;
  onDeleteValue: (index: number) => void;
  errors?: ChangeableFieldErrors;
}

function ChangeableFieldTable<T>({
  values,
  dataName,
  errors,
  tableOptions = {},
  valueColumns,
  onUpdateValue,
  onDeleteValue
}: ChangeableFieldTableProps<T>) {
  const handleChangeStartDate = useCallback(
    (index: number, newStartDate: DateTime | null) => {
      onUpdateValue(index, {
        ...values[index],
        startDate: newStartDate ? newStartDate.startOf("day") : null
      });

      const previousRowEndDate = index > 0 ? values[index - 1].endDate : null;

      if (previousRowEndDate && newStartDate) {
        const newPreviousRowEndDate = newStartDate
          .minus({ day: 1 })
          .startOf("day");

        onUpdateValue(index - 1, {
          ...values[index - 1],
          endDate: newPreviousRowEndDate
        });
      }
    },
    [onUpdateValue, values]
  );

  const handleChangeEndDate = useCallback(
    (index: number, newEndDate: DateTime | null) => {
      let adjustedEndDate = newEndDate;

      if (newEndDate) {
        adjustedEndDate = newEndDate.startOf("day");
      }

      onUpdateValue(index, {
        ...values[index],
        endDate: adjustedEndDate
      });
    },
    [onUpdateValue, values]
  );

  const getErrorForIndex = useCallback(
    (index: number) => {
      if (!errors) {
        return undefined;
      }

      if (errors[index] && errors[index]["formula"]) {
        return errors[index]["formula"].map((val, mapIndex) => (
          <span
            className="table-error-message"
            key={index.toString() + "/" + mapIndex.toString()}
          >
            {val}
          </span>
        ));
      }
      return undefined;
    },
    [errors]
  );

  const handleChangeValue = useCallback(
    (index: number, value: T) => {
      onUpdateValue(index, {
        ...values[index],
        value: value
      });
    },
    [values, onUpdateValue]
  );

  const columns: Array<MRT_ColumnDef<ChangeableFieldData<T>>> = useMemo(
    () => [
      getStartDateColumn<T>(values, handleChangeStartDate, tableOptions),
      getEndDateColumn<T>(handleChangeEndDate, tableOptions),
      ...(valueColumns
        ? valueColumns.map((valueColumn) => ({
            ...valueColumn,
            Cell: (props) =>
              valueColumn.Cell
                ? valueColumn.Cell(props, handleChangeValue)
                : undefined
          }))
        : [
            getDefaultValueColumn<T>(
              handleChangeValue,
              getErrorForIndex,
              tableOptions
            )
          ])
    ],
    [
      tableOptions,
      values,
      handleChangeStartDate,
      handleChangeEndDate,
      handleChangeValue,
      getErrorForIndex,
      valueColumns
    ]
  );

  const mrtTable = useMantineReactTable<ChangeableFieldData<T>>({
    ...getMinimalMRTOptions({
      className: "ChangeableFieldTable",
      emptyRowsFallbackText: `Keine ${dataName} vorhanden`
    }),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        delete: {
          text: tableOptions.deleteTooltipText ?? `Reihe löschen`,
          enableDelete: ({ row }) =>
            row.index !== 0 || !!tableOptions.allowDeletionOfAllRows,
          onClick: (row) => onDeleteValue(row.index)
        }
      }
    }),
    columns: columns,
    data: values
  });

  return <MantineReactTable table={mrtTable} />;
}

export { ChangeableFieldTable, ChangeableFieldTableProps };
